
/**
 * QuickPages Behaviors v17 — 2026-06-07
 * Provides interactive functionality for QuickPages content sections.
 *
 * ⚠️  IMPORTANT: DUPLICATE FILE WARNING ⚠️
 * =========================================
 * This file MUST be kept in sync with:
 * → libs/shared/quickpages-behaviors.ts
 *
 * These files serve different purposes:
 * - THIS FILE (extensions): Used by live Shopify storefront via app embed block
 * - THE OTHER FILE (libs/shared): Used by React preview components (PageBuilder, Fullscreen)
 *
 * When adding or modifying behaviors, you MUST update BOTH files manually.
 * The duplication exists because:
 * 1. This extension file must be plain JS (no build step in theme extensions)
 * 2. The shared file exports a string for injection into preview iframes
 * =========================================
 */
(function() {
  'use strict';

  // ===== VARIANT PILL ACTIVE-STATE SWAP =====
  // Reads the active/inactive class lists from the nearest ancestor carrying
  // the data-qp-variant-active attribute (typically the option-group div).
  // Each list is layered ON TOP of the pill's base structural classes:
  //   - clicked pill: add active classes, remove inactive classes
  //   - sibling pills (same scope): add inactive, remove active
  // No "derive defaults" magic — the AI emits explicit state classes, the
  // runtime just toggles them. Hover styling is pure CSS via Tailwind
  // hover:* classes that the AI puts in the pill's base class list.
  // No-op if the attribute isn't present (defensive — older sections).
  function qpSwapVariantActiveClasses(clickedEl, scope, pillSelector) {
    var srcEl = (scope || clickedEl).closest('[data-qp-variant-active], [data-qp-variant-inactive]');
    if (!srcEl) return;
    var activeAttr = srcEl.getAttribute('data-qp-variant-active') || '';
    var inactiveAttr = srcEl.getAttribute('data-qp-variant-inactive') || '';
    var activeList = activeAttr.split(/\s+/).filter(Boolean);
    var inactiveList = inactiveAttr.split(/\s+/).filter(Boolean);
    if (activeList.length === 0 && inactiveList.length === 0) return;

    // Idempotent by construction: for EACH pill, remove both sets first,
    // then apply the correct one based on whether it's the clicked pill.
    // The previous two-pass implementation (all-pills → make inactive;
    // clicked pill → make active) could leave the clicked pill with
    // BOTH sets if something external re-added classes between the two
    // passes. That produced invisible text via CSS source-order wins
    // (both qp-bg-[#hex] rules coexist in the stylesheet; the later one
    // wins). Always resetting both sets before re-applying eliminates
    // the race regardless of how many times this function runs or what
    // other scripts mutate the DOM between calls.
    (scope || clickedEl.parentNode).querySelectorAll(pillSelector).forEach(function(pill) {
      activeList.forEach(function(cls) { pill.classList.remove(cls); });
      inactiveList.forEach(function(cls) { pill.classList.remove(cls); });
      var target = (pill === clickedEl) ? activeList : inactiveList;
      target.forEach(function(cls) { pill.classList.add(cls); });
    });
  }

  // ===== CART DRAWER INTEGRATIONS =====
  // Theme-specific functions to refresh/open the cart drawer after Ajax Add to Cart.
  // The merchant's theme is identified via the chat agent and stored as a shop-level setting.
  // At publish time, the theme ID is baked into data-qp-cart-theme on the buybox container.
  var CART_INTEGRATIONS = {
    // --- Cart apps (take priority over themes) ---
    rebuy: function() { window.Rebuy.SmartCart.show(); },
    slidecart: function() { window.SLIDECART_UPDATE(); window.SLIDECART_OPEN(); },
    upcart: function() { window.upcartRefreshCart(); },
    monster: function() { window.monster_refresh(); },

    // --- Themes ---
    dawn: function() {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: {}, sections: ['cart-drawer', 'cart-icon-bubble'] })
      })
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var d = document.querySelector('cart-drawer');
          if (d && d.renderContents) { d.renderContents(cart); d.classList.remove('is-empty'); }
        });
    },
    sense: function() {
      // Sense uses the same cart system as Dawn
      CART_INTEGRATIONS.dawn();
    },
    horizon: function() {
      var d = document.querySelector('cart-drawer-component');
      import('@theme/events').then(function(mod) {
        fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
          document.dispatchEvent(new mod.CartUpdateEvent(cart, 'manual-trigger', {
            itemCount: cart.item_count, source: 'quickpages', sections: {}
          }));
          if (d && typeof d.open === 'function') d.open();
        });
      }).catch(function() {});
    },
    focal: function() {
      fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, openMiniCart: true } })
        );
      });
    },
    sleek: function() {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    },
    impact: function() {
      var d = document.querySelector('#cart-drawer');
      fetch('/?section_id=cart-drawer')
        .then(function(r) { return r.text(); })
        .then(function(text) {
          var parsed = new DOMParser().parseFromString(text, 'text/html');
          var inner = parsed.getElementById('cart-drawer');
          if (inner && d) { d.innerHTML = inner.innerHTML; d.setAttribute('open', ''); }
        });
    },
    flex: function() { window.Shopify.theme.jsAjaxCart.showDrawer(); window.Shopify.theme.jsAjaxCart.updateView(); },
    debutify: function() { window.theme.ajaxCart.update(); },
    minimog: function() { document.dispatchEvent(new CustomEvent('update-cart-drawer')); },
  };

  function syncCartWithTheme(themeId) {
    var fn = CART_INTEGRATIONS[themeId];
    if (fn) {
      try { fn(); } catch(e) { /* silent — integration failed, item still added */ }
      return;
    }
    // Unknown theme — best-effort badge update
    fetch('/cart.json')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        document.querySelectorAll('.cart-count-bubble span, [data-cart-count], .cart-count')
          .forEach(function(el) { el.textContent = String(cart.item_count); });
      }).catch(function() {});
  }

  var QuickPagesBehaviors = {

    // ===== ACCORDION BEHAVIOR =====
    accordion: {
      selector: '[data-qp-accordion]',
      init: function(container) {
        container.addEventListener('click', function(event) {
          var button = event.target.closest('[data-qp-accordion-toggle]');
          if (!button) return;

          var item = button.closest('[data-qp-accordion-item]');
          if (!item) return;

          var isActive = item.classList.contains('active');
          var allowMultiple = container.hasAttribute('data-qp-accordion-multiple');

          // Close others unless multiple mode is enabled
          if (!allowMultiple) {
            var allItems = container.querySelectorAll('[data-qp-accordion-item]');
            allItems.forEach(function(otherItem) {
              if (otherItem !== item) {
                otherItem.classList.remove('active');
                var otherButton = otherItem.querySelector('[data-qp-accordion-toggle]');
                if (otherButton) {
                  otherButton.setAttribute('aria-expanded', 'false');
                }
              }
            });
          }

          // Toggle current item
          if (isActive) {
            item.classList.remove('active');
            button.setAttribute('aria-expanded', 'false');
          } else {
            item.classList.add('active');
            button.setAttribute('aria-expanded', 'true');
          }
        });
      }
    },

    // ===== COUNTDOWN TIMER =====
    countdown: {
      selector: '[data-qp-countdown]',
      init: function(container) {
        var endDateStr = container.getAttribute('data-qp-countdown-end');
        if (!endDateStr) {
          console.warn('[QuickPages Countdown] No end date specified');
          return;
        }

        var endDate = new Date(endDateStr);
        if (isNaN(endDate.getTime())) {
          console.warn('[QuickPages Countdown] Invalid end date:', endDateStr);
          return;
        }

        // Cache DOM element references
        var elements = {
          days: container.querySelector('[data-qp-countdown-days]'),
          hours: container.querySelector('[data-qp-countdown-hours]'),
          minutes: container.querySelector('[data-qp-countdown-minutes]'),
          seconds: container.querySelector('[data-qp-countdown-seconds]')
        };

        // Validate all elements exist
        if (!elements.days || !elements.hours || !elements.minutes || !elements.seconds) {
          console.warn('[QuickPages Countdown] Missing display elements');
          return;
        }

        var intervalId = null;

        function padNumber(num) {
          return num < 10 ? '0' + num : String(num);
        }

        function updateCountdown() {
          var now = new Date().getTime();
          var distance = endDate.getTime() - now;

          if (distance <= 0) {
            // Countdown expired
            elements.days.textContent = '00';
            elements.hours.textContent = '00';
            elements.minutes.textContent = '00';
            elements.seconds.textContent = '00';
            container.classList.add('quickpages-countdown-expired');

            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
            return;
          }

          // Calculate time units
          var days = Math.floor(distance / (1000 * 60 * 60 * 24));
          var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
          var seconds = Math.floor((distance % (1000 * 60)) / 1000);

          // Update display
          elements.days.textContent = padNumber(days);
          elements.hours.textContent = padNumber(hours);
          elements.minutes.textContent = padNumber(minutes);
          elements.seconds.textContent = padNumber(seconds);
        }

        // Initial update
        updateCountdown();

        // Update every second
        intervalId = setInterval(updateCountdown, 1000);

        // Store interval ID for potential cleanup
        container._qpCountdownInterval = intervalId;
      }
    },

    // ===== CAROUSEL =====
    carousel: {
      selector: '[data-qp-carousel]',
      init: function(container) {
        var track = container.querySelector('[data-qp-carousel-track]');
        var prevBtn = container.querySelector('[data-qp-carousel-prev]');
        var nextBtn = container.querySelector('[data-qp-carousel-next]');

        if (!track) {
          console.warn('[QuickPages Carousel] Missing track element');
          return;
        }

        var scrollAmount = 300;

        function updateButtonStates() {
          if (!prevBtn || !nextBtn) return;

          var scrollLeft = track.scrollLeft;
          var maxScroll = track.scrollWidth - track.clientWidth;

          prevBtn.disabled = scrollLeft <= 0;
          nextBtn.disabled = scrollLeft >= maxScroll - 1;
        }

        if (prevBtn) {
          prevBtn.addEventListener('click', function() {
            track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', function() {
            track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
          });
        }

        track.addEventListener('scroll', updateButtonStates);

        // Initial state
        updateButtonStates();

        // Store reference for potential cleanup
        container._qpCarouselTrack = track;
      }
    },

    // ===== BUY BOX =====
    buybox: {
      selector: '[data-qp-buybox]',
      init: function(container) {
        // --- Read variant + product data ---
        var variantJsonEl = container.querySelector('[data-qp-variant-json]');
        var productJsonEl = container.querySelector('[data-qp-product-json]');
        var variants = [];
        var productData = {};
        try { variants = JSON.parse(variantJsonEl ? variantJsonEl.textContent : '[]'); } catch(e) {}
        try { productData = JSON.parse(productJsonEl ? productJsonEl.textContent : '{}'); } catch(e) {}

        // --- Track selected options ---
        var selectedOptions = {};
        // Pre-select from URL ?variant= parameter
        var urlParams = new URLSearchParams(window.location.search);
        var urlVariantId = urlParams.get('variant');
        var currentVariant = null;

        if (urlVariantId && variants.length > 0) {
          currentVariant = variants.find(function(v) { return String(v.id) === urlVariantId; });
        }
        if (!currentVariant && variants.length > 0) {
          currentVariant = variants[0];
        }

        if (currentVariant) {
          if (currentVariant.option1) selectedOptions['1'] = currentVariant.option1;
          if (currentVariant.option2) selectedOptions['2'] = currentVariant.option2;
          if (currentVariant.option3) selectedOptions['3'] = currentVariant.option3;
        }

        // --- DOM references ---
        var priceEl = container.querySelector('[data-qp-price-target]');
        var compareEl = container.querySelector('[data-qp-compare-target]');
        var stockEl = container.querySelector('[data-qp-stock-target]');
        var formIdInput = container.querySelector('form input[name="id"]');
        var cartBtn = container.querySelector('form button[type="submit"]');
        var galleryMain = container.querySelector('[data-qp-gallery-main] img');
        var galleryThumbs = container.querySelector('[data-qp-gallery-thumbs]');
        var quantityInput = container.querySelector('[data-qp-quantity-input]');
        var quantityMinus = container.querySelector('[data-qp-quantity-minus]');
        var quantityPlus = container.querySelector('[data-qp-quantity-plus]');
        var formQuantityInput = container.querySelector('form input[name="quantity"]');

        // --- Helper: format price in cents to dollars ---
        function formatMoney(cents) {
          if (cents == null) return '';
          var dollars = (cents / 100).toFixed(2);
          return '$' + dollars;
        }

        // --- Helper: find variant matching selected options ---
        function findVariant() {
          return variants.find(function(v) {
            var match = true;
            if (selectedOptions['1'] && v.option1 !== selectedOptions['1']) match = false;
            if (selectedOptions['2'] && v.option2 !== selectedOptions['2']) match = false;
            if (selectedOptions['3'] && v.option3 !== selectedOptions['3']) match = false;
            return match;
          }) || null;
        }

        // --- Helper: update all displays for a variant ---
        function applyVariant(variant) {
          if (!variant) return;
          currentVariant = variant;

          // Update price
          if (priceEl) {
            priceEl.textContent = formatMoney(variant.price);
          }

          // Update compare-at price
          if (compareEl) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              compareEl.textContent = formatMoney(variant.compare_at_price);
              compareEl.style.display = '';
            } else {
              compareEl.style.display = 'none';
            }
          }

          // Update stock status
          if (stockEl) {
            if (variant.available) {
              stockEl.innerHTML = '<span style="color: #16a34a;">In Stock</span>';
            } else {
              stockEl.innerHTML = '<span style="color: #dc2626;">Sold Out</span>';
            }
          }

          // Update cart button
          if (cartBtn) {
            cartBtn.disabled = !variant.available;
            if (!variant.available) {
              cartBtn.textContent = 'Sold Out';
            } else {
              cartBtn.textContent = 'Add to Cart';
            }
          }

          // Update hidden form input
          if (formIdInput) {
            formIdInput.value = String(variant.id);
          }

          // Update gallery main image
          if (galleryMain && variant.featured_image && variant.featured_image.src) {
            galleryMain.src = variant.featured_image.src;
          }

          // Update URL
          if (window.history && window.history.replaceState) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', String(variant.id));
            window.history.replaceState({}, '', url.toString());
          }

          // Dispatch event
          container.dispatchEvent(new CustomEvent('qp:variant-changed', {
            bubbles: true,
            detail: { variant: variant }
          }));
        }

        // --- Variant link click handler ---
        container.addEventListener('click', function(event) {
          // Handle variant pill clicks
          var link = event.target.closest('a[href*="variant="]');
          if (link && variants.length > 0) {
            event.preventDefault();

            // Determine which option group this pill belongs to
            var optionGroup = link.closest('[data-option-position]');
            var optionPosition = optionGroup ? optionGroup.getAttribute('data-option-position') : null;

            // Extract variant ID from href
            var href = link.getAttribute('href') || '';
            var variantMatch = href.match(/variant=(d+)/);
            if (variantMatch) {
              var clickedVariant = variants.find(function(v) { return String(v.id) === variantMatch[1]; });
              if (clickedVariant) {
                // Only update the option for the group that was clicked
                if (optionPosition) {
                  var optKey = 'option' + optionPosition;
                  selectedOptions[optionPosition] = clickedVariant[optKey];
                } else {
                  // Fallback: update all options from clicked variant
                  if (clickedVariant.option1) selectedOptions['1'] = clickedVariant.option1;
                  if (clickedVariant.option2) selectedOptions['2'] = clickedVariant.option2;
                  if (clickedVariant.option3) selectedOptions['3'] = clickedVariant.option3;
                }

                // Find the exact variant matching all selected options
                var resolvedVariant = findVariant() || clickedVariant;
                applyVariant(resolvedVariant);

                // Update active state on pills within the same option group.
                // Contract: data-qp-variant-active and data-qp-variant-inactive
                // each list classes that are LAYERED on top of the pill's
                // base structural classes — never overlap with the base.
                qpSwapVariantActiveClasses(link, optionGroup || container, 'a[href*="variant="]');
              }
            }
            return;
          }

          // Handle variant button clicks (preview mode — buttons with data-qp-variant-value, not anchor tags)
          var variantBtn = event.target.closest('[data-qp-variant-value]');
          if (variantBtn && !link && variants.length > 0) {
            event.preventDefault();

            // Get the clicked value text
            var clickedValue = (variantBtn.textContent || '').trim();
            if (!clickedValue) return;

            // Determine which option group this button belongs to
            var btnOptionGroup = variantBtn.closest('[data-qp-variant-option]');
            var optionName = btnOptionGroup ? btnOptionGroup.getAttribute('data-qp-variant-option') : null;

            // Find the option position from the product options array
            var optPos = null;
            if (optionName && productData.options) {
              var optIndex = productData.options.indexOf(optionName);
              if (optIndex >= 0) optPos = String(optIndex + 1);
            }
            // Fallback: try to infer position from DOM order of option groups
            if (!optPos && btnOptionGroup) {
              var allOptionGroups = container.querySelectorAll('[data-qp-variant-option]');
              for (var gi = 0; gi < allOptionGroups.length; gi++) {
                if (allOptionGroups[gi] === btnOptionGroup) {
                  optPos = String(gi + 1);
                  break;
                }
              }
            }

            // Update the selected option
            if (optPos) {
              selectedOptions[optPos] = clickedValue;
            }

            // Find matching variant
            var matchedVariant = findVariant();
            if (matchedVariant) {
              applyVariant(matchedVariant);
            }

            // Update active state within the same option group. See the
            // anchor-pill handler above for the contract details — same
            // semantics apply to button-style variant pills (preview mode).
            qpSwapVariantActiveClasses(variantBtn, btnOptionGroup || container, '[data-qp-variant-value]');
            return;
          }

          // Handle thumbnail clicks
          var thumb = event.target.closest('[data-qp-gallery-thumbs] img');
          if (thumb && galleryMain) {
            var fullSrc = thumb.getAttribute('data-full-src') || thumb.src;
            galleryMain.src = fullSrc;

            // Update active state on thumbnails
            if (galleryThumbs) {
              galleryThumbs.querySelectorAll('img').forEach(function(t) {
                t.classList.remove('qp-thumb-active');
              });
              thumb.classList.add('qp-thumb-active');
            }
            return;
          }

          // Handle lightbox click
          var lightboxTarget = event.target.closest('[data-qp-lightbox]');
          if (lightboxTarget) {
            event.preventDefault();
            var imgSrc = lightboxTarget.tagName === 'IMG' ? lightboxTarget.src : (lightboxTarget.querySelector('img') || {}).src;
            if (!imgSrc) return;

            // Collect all product images for navigation
            var allImages = [];
            if (galleryThumbs) {
              galleryThumbs.querySelectorAll('img').forEach(function(t) {
                allImages.push(t.getAttribute('data-full-src') || t.src);
              });
            }
            if (allImages.length === 0) allImages.push(imgSrc);

            // Find current index
            var currentIdx = 0;
            for (var li = 0; li < allImages.length; li++) {
              if (imgSrc.indexOf(allImages[li]) !== -1 || allImages[li].indexOf(imgSrc.split('?')[0]) !== -1) {
                currentIdx = li;
                break;
              }
            }

            // Create overlay
            var overlay = document.createElement('div');
            overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.92);display:flex;align-items:center;justify-content:center;cursor:zoom-out;';
            var lightboxImg = document.createElement('img');
            lightboxImg.src = allImages[currentIdx];
            lightboxImg.style.cssText = 'max-width:90vw;max-height:90vh;object-fit:contain;cursor:default;';
            lightboxImg.alt = 'Product image';
            overlay.appendChild(lightboxImg);

            // Navigation arrows (if multiple images)
            if (allImages.length > 1) {
              var arrowStyle = 'position:absolute;top:50%;transform:translateY(-50%);background:rgba(255,255,255,0.15);color:white;border:none;font-size:2rem;padding:0.5rem 1rem;cursor:pointer;z-index:100000;line-height:1;';
              var prevArrow = document.createElement('button');
              prevArrow.innerHTML = '‹';
              prevArrow.style.cssText = arrowStyle + 'left:1rem;';
              prevArrow.setAttribute('aria-label', 'Previous image');
              var nextArrow = document.createElement('button');
              nextArrow.innerHTML = '›';
              nextArrow.style.cssText = arrowStyle + 'right:1rem;';
              nextArrow.setAttribute('aria-label', 'Next image');

              prevArrow.addEventListener('click', function(e) {
                e.stopPropagation();
                currentIdx = (currentIdx - 1 + allImages.length) % allImages.length;
                lightboxImg.src = allImages[currentIdx];
              });
              nextArrow.addEventListener('click', function(e) {
                e.stopPropagation();
                currentIdx = (currentIdx + 1) % allImages.length;
                lightboxImg.src = allImages[currentIdx];
              });

              overlay.appendChild(prevArrow);
              overlay.appendChild(nextArrow);
            }

            // Close on overlay click or Escape
            overlay.addEventListener('click', function(e) {
              if (e.target === overlay) {
                document.body.removeChild(overlay);
                document.removeEventListener('keydown', keyHandler);
              }
            });
            var keyHandler = function(e) {
              if (e.key === 'Escape') {
                document.body.removeChild(overlay);
                document.removeEventListener('keydown', keyHandler);
              } else if (e.key === 'ArrowLeft' && allImages.length > 1) {
                currentIdx = (currentIdx - 1 + allImages.length) % allImages.length;
                lightboxImg.src = allImages[currentIdx];
              } else if (e.key === 'ArrowRight' && allImages.length > 1) {
                currentIdx = (currentIdx + 1) % allImages.length;
                lightboxImg.src = allImages[currentIdx];
              }
            };
            document.addEventListener('keydown', keyHandler);
            document.body.appendChild(overlay);
            return;
          }

          // Handle quantity buttons
          if (event.target.closest('[data-qp-quantity-minus]') && quantityInput) {
            var val = parseInt(quantityInput.value, 10) || 1;
            if (val > 1) {
              quantityInput.value = String(val - 1);
              if (formQuantityInput) formQuantityInput.value = String(val - 1);
            }
            return;
          }
          if (event.target.closest('[data-qp-quantity-plus]') && quantityInput) {
            var val2 = parseInt(quantityInput.value, 10) || 1;
            quantityInput.value = String(val2 + 1);
            if (formQuantityInput) formQuantityInput.value = String(val2 + 1);
            return;
          }
        });

        // --- Cart form handler ---
        var form = container.querySelector('form[action="/cart/add"]');
        if (form) {
          form.addEventListener('submit', function(event) {
            var cartResponse = (cartBtn && cartBtn.getAttribute('data-qp-cart-response')) || 'redirect';

            // Redirect: let the native form POST handle it — no JS needed
            if (cartResponse === 'redirect') return;

            event.preventDefault();

            var formData = new FormData(form);
            var themeId = container.getAttribute('data-qp-cart-theme') || '';

            // Show loading state
            var originalText = '';
            if (cartBtn) {
              originalText = cartBtn.textContent;
              cartBtn.textContent = 'Adding...';
              cartBtn.disabled = true;
            }

            fetch('/cart/add.js', {
              method: 'POST',
              body: formData,
            })
            .then(function(res) { return res.json(); })
            .then(function(data) {
              // Sync cart state with the theme (opens drawer if applicable)
              syncCartWithTheme(themeId);

              if (cartResponse === 'drawer') {
                // Announce the add so the QP-native cart drawer can
                // re-render its contents (Section Rendering API) and open.
                // The cartDrawer behaviour listens for this on document.
                document.dispatchEvent(new CustomEvent('qp:cart:added', { detail: data }));
                // Drawer is the feedback — just restore the button
                if (cartBtn) {
                  cartBtn.textContent = originalText;
                  cartBtn.disabled = false;
                }
              } else if (cartResponse === 'toast') {
                if (cartBtn) {
                  cartBtn.textContent = 'Added to Cart ✓';
                  setTimeout(function() {
                    cartBtn.textContent = originalText;
                    cartBtn.disabled = false;
                  }, 2000);
                }
              } else {
                // inline — show confirmation
                if (cartBtn) {
                  cartBtn.textContent = 'Added ✓';
                  setTimeout(function() {
                    cartBtn.textContent = originalText;
                    cartBtn.disabled = false;
                  }, 2000);
                }
              }
            })
            .catch(function() {
              // Restore button and fallback: submit normally
              if (cartBtn) {
                cartBtn.textContent = originalText;
                cartBtn.disabled = false;
              }
              form.submit();
            });
          });
        }
      }
    },

    // ===== SCROLLING TICKER =====
    ticker: {
      selector: '[data-qp-ticker]',
      init: function(container) {
        var speedAttr = container.getAttribute('data-qp-ticker-speed');
        var speedMultiplier = parseFloat(speedAttr) || 1;

        // Base duration in seconds (30s at speed 1)
        var baseDuration = 30;
        var actualDuration = baseDuration / speedMultiplier;

        // Find the ticker track element
        var track = container.querySelector('.quickpages-ticker-track');
        if (!track) {
          console.warn('[QuickPages Ticker] Missing .quickpages-ticker-track element');
          return;
        }

        // Apply the calculated animation duration
        track.style.animationDuration = actualDuration + 's';

        // Ensure we have enough content for seamless looping
        var contents = track.querySelectorAll('.quickpages-ticker-content');
        if (contents.length < 2) {
          // Clone the content if we only have one set
          var firstContent = track.querySelector('.quickpages-ticker-content');
          if (firstContent) {
            var clone = firstContent.cloneNode(true);
            track.appendChild(clone);
          }
        }

        // Store reference for potential cleanup
        container._qpTickerTrack = track;
      }
    },

    // ===== TABS =====
    tabs: {
      selector: '[data-qp-tabs]',
      init: function(container) {
        // Read active-state Tailwind classes from data-qp-tab-active
        var tabActiveAttr = container.getAttribute('data-qp-tab-active');
        var tabActiveClasses = tabActiveAttr ? tabActiveAttr.split(/\s+/) : [];
        var tabDefaultClasses = [];

        // Snapshot default classes from the second trigger (first is styled as active by the AI)
        if (tabActiveClasses.length > 0) {
          var allTriggers = container.querySelectorAll('[data-qp-tab-trigger]');
          var refTrigger = allTriggers.length > 1 ? allTriggers[1] : allTriggers[0];
          if (refTrigger) {
            tabActiveClasses.forEach(function(cls) {
              var bIdx = cls.indexOf('[');
              var pfx = bIdx > 0 ? cls.substring(0, bIdx) : cls;
              if (pfx) {
                Array.from(refTrigger.classList).forEach(function(existing) {
                  if (existing.indexOf(pfx) === 0 && existing !== cls) {
                    tabDefaultClasses.push(existing);
                  }
                });
              }
            });
          }
        }

        function activateTab(triggerEl) {
          var panelId = triggerEl.getAttribute('data-qp-tab-trigger');
          if (!panelId) return;

          // Deactivate all triggers and hide all panels
          container.querySelectorAll('[data-qp-tab-trigger]').forEach(function(t) {
            t.classList.remove('active');
            t.setAttribute('aria-selected', 'false');
            // Swap to default classes
            tabActiveClasses.forEach(function(cls) { t.classList.remove(cls); });
            tabDefaultClasses.forEach(function(cls) { t.classList.add(cls); });
          });
          container.querySelectorAll('[data-qp-tab-panel]').forEach(function(p) {
            p.classList.remove('active');
            p.style.display = 'none';
          });

          // Activate clicked trigger and show matching panel
          triggerEl.classList.add('active');
          triggerEl.setAttribute('aria-selected', 'true');
          tabDefaultClasses.forEach(function(cls) { triggerEl.classList.remove(cls); });
          tabActiveClasses.forEach(function(cls) { triggerEl.classList.add(cls); });
          var panel = container.querySelector('[data-qp-tab-panel="' + panelId + '"]');
          if (panel) {
            panel.classList.add('active');
            panel.style.display = '';
          }
        }

        container.addEventListener('click', function(event) {
          var trigger = event.target.closest('[data-qp-tab-trigger]');
          if (trigger) activateTab(trigger);
        });

        // Initialize: activate the first tab
        var firstTrigger = container.querySelector('[data-qp-tab-trigger]');
        if (firstTrigger) activateTab(firstTrigger);
      }
    },

    // ===== MOBILE NAVIGATION (Full Store Builder — Track E) =====
    // Activated by the header variant. Wires the burger trigger to the
    // mobile-drawer variant elsewhere in the document. Backdrop click
    // closes; body scroll is locked while open.
    mobileNav: {
      selector: '[data-qp-nav="header"]',
      init: function(header) {
        var trigger = header.querySelector('[data-qp-burger-trigger]');
        if (!trigger) return;
        var drawer = document.querySelector('[data-qp-nav="mobile-drawer"]');
        if (!drawer) return;
        var OPEN_CLASS = 'qp-mobile-nav-open';
        var LOCK_CLASS = 'qp-no-scroll';
        var backdrop = null;
        function openDrawer() {
          drawer.classList.add(OPEN_CLASS);
          drawer.setAttribute('aria-hidden', 'false');
          trigger.setAttribute('aria-expanded', 'true');
          document.body.classList.add(LOCK_CLASS);
          if (!backdrop) {
            backdrop = document.createElement('div');
            backdrop.setAttribute('data-qp-mobile-nav-backdrop', '');
            backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:40;';
            backdrop.addEventListener('click', closeDrawer);
            document.body.appendChild(backdrop);
          }
        }
        function closeDrawer() {
          drawer.classList.remove(OPEN_CLASS);
          drawer.setAttribute('aria-hidden', 'true');
          trigger.setAttribute('aria-expanded', 'false');
          document.body.classList.remove(LOCK_CLASS);
          if (backdrop && backdrop.parentNode) {
            backdrop.parentNode.removeChild(backdrop);
            backdrop = null;
          }
        }
        trigger.addEventListener('click', function() {
          if (drawer.classList.contains(OPEN_CLASS)) {
            closeDrawer();
          } else {
            openDrawer();
          }
        });
        // Close on inner [data-qp-mobile-drawer-close] click or on ESC.
        drawer.addEventListener('click', function(event) {
          if (event.target.closest('[data-qp-mobile-drawer-close]')) {
            closeDrawer();
          }
        });
        document.addEventListener('keydown', function(event) {
          if (event.key === 'Escape' && drawer.classList.contains(OPEN_CLASS)) {
            closeDrawer();
          }
        });
      }
    },

    // ===== CART DRAWER (Full Store Builder — Track E) =====
    // Opens our generated cart drawer ([data-qp-cart="drawer"]) when the
    // visitor clicks a [data-qp-cart-icon] anywhere on the page. Defers
    // to CART_INTEGRATIONS when the merchant has a cart app configured
    // (data-qp-cart-app baked onto the icon by the publish pipeline).
    cartDrawer: {
      selector: '[data-qp-cart="drawer"]',
      init: function(drawer) {
        var OPEN_CLASS = 'qp-cart-drawer-open';
        var LOCK_CLASS = 'qp-no-scroll';
        var backdrop = null;
        // Section id used by the Section Rendering API to re-render the
        // drawer server-side after the cart changes. Defaults to the
        // Store Builder convention; overridable via data-qp-cart-section.
        var SECTION_ID = drawer.getAttribute('data-qp-cart-section') || 'qp-cart-drawer';

        // Re-fetch the drawer section with the current cart and swap its
        // inner HTML in. The cart is server state, so this keeps the items,
        // quantities, and subtotal (incl. discounts / currency) correct
        // without re-implementing Shopify's money logic client-side. We
        // swap innerHTML (not the element) so the open state + the
        // delegated listeners bound on 'drawer' survive the refresh.
        function refreshDrawer() {
          return fetch('/?section_id=' + encodeURIComponent(SECTION_ID), { credentials: 'same-origin' })
            .then(function(r) { return r.text(); })
            .then(function(html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var fresh = doc.querySelector('[data-qp-cart="drawer"]');
              if (fresh) drawer.innerHTML = fresh.innerHTML;
            })
            .catch(function() { /* leave existing contents on failure */ });
        }

        // Update one line's quantity on the server, then re-render. quantity
        // 0 removes the line. Fails safe — a missing key just no-ops.
        function changeLine(key, quantity) {
          if (!key) return;
          fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: key, quantity: quantity }),
            credentials: 'same-origin'
          })
            .then(function() { return refreshDrawer(); })
            .catch(function() {});
        }

        function open() {
          drawer.classList.add(OPEN_CLASS);
          drawer.setAttribute('aria-hidden', 'false');
          document.body.classList.add(LOCK_CLASS);
          if (!backdrop) {
            backdrop = document.createElement('div');
            backdrop.setAttribute('data-qp-cart-drawer-backdrop', '');
            backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:40;';
            backdrop.addEventListener('click', close);
            document.body.appendChild(backdrop);
          }
        }
        function close() {
          drawer.classList.remove(OPEN_CLASS);
          drawer.setAttribute('aria-hidden', 'true');
          document.body.classList.remove(LOCK_CLASS);
          if (backdrop && backdrop.parentNode) {
            backdrop.parentNode.removeChild(backdrop);
            backdrop = null;
          }
        }

        // Hook every cart icon on the page. Each icon may carry
        // data-qp-cart-app="rebuy|slidecart|upcart|..." which tells us to
        // defer to the merchant's existing app instead of opening our drawer.
        document.querySelectorAll('[data-qp-cart-icon]').forEach(function(icon) {
          icon.addEventListener('click', function(event) {
            var appId = icon.getAttribute('data-qp-cart-app');
            if (appId && CART_INTEGRATIONS[appId]) {
              event.preventDefault();
              CART_INTEGRATIONS[appId]();
              return;
            }
            event.preventDefault();
            open();
            // The drawer was server-rendered at page load; sync it in case
            // the cart changed since (e.g. add on a previous page).
            refreshDrawer();
          });
        });

        // Delegated in-drawer interactions. Listeners live on 'drawer'
        // (stable) so they survive refreshDrawer()'s innerHTML swap.
        drawer.addEventListener('click', function(event) {
          // Close (the X / any element marked as the close control).
          if (event.target.closest('[data-qp-cart-drawer-close]')) {
            close();
            return;
          }
          // Quantity +/- — adjust the line on the server, then re-render.
          var qtyBtn = event.target.closest('[data-qp-cart-item-quantity] [data-qp-cart-qty]');
          if (qtyBtn) {
            event.preventDefault();
            var line = qtyBtn.closest('[data-qp-cart-line-key]');
            if (!line) return; // fail safe — unwired stepper does nothing
            var key = line.getAttribute('data-qp-cart-line-key');
            var qty = parseInt(line.getAttribute('data-qp-cart-line-qty'), 10) || 0;
            var delta = qtyBtn.getAttribute('data-qp-cart-qty') === 'up' ? 1 : -1;
            changeLine(key, Math.max(0, qty + delta));
            return;
          }
          // Remove — item.url_to_remove points at /cart/change. Intercept so
          // we stay in the drawer (re-render) instead of navigating to /cart.
          var removeLink = event.target.closest('a[href*="/cart/change"]');
          if (removeLink) {
            event.preventDefault();
            fetch(removeLink.getAttribute('href'), { credentials: 'same-origin' })
              .then(function() { return refreshDrawer(); })
              .catch(function() {});
            return;
          }
        });
        document.addEventListener('keydown', function(event) {
          if (event.key === 'Escape' && drawer.classList.contains(OPEN_CLASS)) {
            close();
          }
        });

        // After a successful add-to-cart submission from a buy box on the
        // same page, the buy-box runtime dispatches 'qp:cart:added' on the
        // document. Open the drawer in response so the visitor sees their
        // newly added item.
        document.addEventListener('qp:cart:added', function() {
          // Defer to a configured cart app if one is registered on any icon.
          var iconWithApp = document.querySelector('[data-qp-cart-icon][data-qp-cart-app]');
          if (iconWithApp) {
            var appId = iconWithApp.getAttribute('data-qp-cart-app');
            if (appId && CART_INTEGRATIONS[appId]) {
              CART_INTEGRATIONS[appId]();
              return;
            }
          }
          // Re-render with the just-added item, THEN open — otherwise the
          // drawer shows its stale page-load contents (the "empty bag" bug).
          refreshDrawer().then(open);
        });
      }
    },

    // ===== FOOTER NEWSLETTER SUBMIT (Full Store Builder — Track E) =====
    // Ajax-submits the footer newsletter form (Shopify customer marketing
    // opt-in) and shows inline success / error without a page reload.
    // Looks for [data-qp-newsletter] on the form element (set by the
    // footer publish-step). Falls back to letting Shopify do a native
    // navigation when fetch fails.
    newsletter: {
      selector: '[data-qp-newsletter]',
      init: function(form) {
        if (form.tagName.toLowerCase() !== 'form') return;
        form.addEventListener('submit', function(event) {
          event.preventDefault();
          var statusEl = form.querySelector('[data-qp-newsletter-status]');
          function showStatus(message, isError) {
            if (statusEl) {
              statusEl.textContent = message;
              statusEl.setAttribute('data-qp-newsletter-error', isError ? 'true' : 'false');
            }
          }
          var data = new FormData(form);
          // Shopify's customer marketing form returns a Shopify-themed
          // success page; we don't render its body, we just check status.
          fetch(form.action, {
            method: form.method || 'POST',
            body: data,
            headers: { 'Accept': 'text/html' },
            credentials: 'same-origin'
          })
            .then(function(response) {
              if (response.ok) {
                showStatus('Thanks for subscribing!', false);
                form.reset();
              } else {
                showStatus('Sign-up failed. Please try again.', true);
              }
            })
            .catch(function() {
              // Network error — let the form submit natively so Shopify
              // shows the visitor a real response.
              showStatus('Connecting…', false);
              form.removeEventListener('submit', arguments.callee);
              form.submit();
            });
        });
      }
    }
  };

  // ===== INITIALIZATION ENGINE =====
  function initializeBehaviors() {
    Object.keys(QuickPagesBehaviors).forEach(function(behaviorName) {
      var behavior = QuickPagesBehaviors[behaviorName];
      var elements = document.querySelectorAll(behavior.selector);

      elements.forEach(function(element) {
        // Prevent double initialization
        if (element.hasAttribute('data-qp-initialized')) return;

        behavior.init(element);
        element.setAttribute('data-qp-initialized', behaviorName);
      });
    });
  }

  // Expose on window so parent frames can re-initialize after innerHTML replacement
  window.__qpInitBehaviors = initializeBehaviors;

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBehaviors);
  } else {
    initializeBehaviors();
  }

  // Shopify theme editor support - reinitialize when sections are loaded
  document.addEventListener('shopify:section:load', initializeBehaviors);

})();
